import pick from 'lodash/pick';
import { initiatePrivileged, transitionPrivileged } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { isBookingProcess, resolveLatestProcessName } from '../../transactions/transaction';
import { createHostBookingBlockedError, isHostUser } from '../../util/bookingRestriction';
import { parse } from '../../util/urlHelpers';
import { getCustomOfferById } from '../../util/customOffers';
import { completeCustomOfferPaymentIfNeeded } from '../../util/customOfferPaymentCompletion';
import {
  RESUME_PAYMENT_PARAM,
  buildResumePaymentCheckoutInitialValues,
  isResumePaymentRequested,
} from './resumePaymentHelpers';
import { getBookingDebugData } from '../../util/bookingData';

const CUSTOM_OFFER_CHECKOUT_LOG = '[custom-offer-checkout]';

const safeId = value => (typeof value === 'string' ? value : value?.uuid);

const debugCustomOfferCheckout = (label, data = {}) => {
  if (typeof console !== 'undefined' && console.debug) {
    console.debug(CUSTOM_OFFER_CHECKOUT_LOG, label, data);
  }
};

const buildCustomOfferOrderData = (offer, listing, config) => {
  const bookingStart = offer?.startDateTime ? new Date(offer.startDateTime) : null;
  const bookingEnd = offer?.endDateTime ? new Date(offer.endDateTime) : null;
  const amount = offer?.totalPrice?.amount || 0;

  return {
    bookingDates: {
      bookingStart,
      bookingEnd,
    },
    customOffer: {
      id: offer.id,
      startDateTime: offer.startDateTime,
      endDateTime: offer.endDateTime,
      totalPrice: offer.totalPrice,
      currency:
        offer.currency ||
        offer.totalPrice?.currency ||
        listing?.attributes?.price?.currency ||
        config?.currency,
      netPayout: offer.netPayout,
      note: offer.note,
    },
    durationType: 'customOffer',
    bookingType: 'customOffer',
    selectedTimeSlots: {
      customOffer: true,
      start: offer.startDateTime,
      end: offer.endDateTime,
    },
    rate: amount / 100,
    isOverTime: false,
    bookingModalDetails: {
      durationType: 'customOffer',
      bookingType: 'customOffer',
      rate: amount / 100,
      selectedTimeSlots: {
        customOffer: true,
        start: offer.startDateTime,
        end: offer.endDateTime,
      },
      isOverTime: false,
    },
  };
};

export const buildCustomOfferCheckoutInitialValues = ({ transaction, listing, offer, config }) => {
  return {
    listing,
    transaction,
    orderData: buildCustomOfferOrderData(offer, listing, config),
    confirmPaymentError: null,
  };
};

export const buildResumePaymentCheckoutValues = ({ transaction, listing }) => {
  return buildResumePaymentCheckoutInitialValues({ transaction, listing });
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

export const INITIATE_INQUIRY_REQUEST = 'app/CheckoutPage/INITIATE_INQUIRY_REQUEST';
export const INITIATE_INQUIRY_SUCCESS = 'app/CheckoutPage/INITIATE_INQUIRY_SUCCESS';
export const INITIATE_INQUIRY_ERROR = 'app/CheckoutPage/INITIATE_INQUIRY_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  orderData: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  isClockInSync: false,
  transaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
  initiateInquiryInProgress: false,
  initiateInquiryError: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS: {
      // Check that the local devices clock is within a minute from the server
      const lastTransitionedAt = payload.transaction?.attributes?.lastTransitionedAt;
      const localTime = new Date();
      const minute = 60000;
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
        isClockInSync: Math.abs(lastTransitionedAt?.getTime() - localTime.getTime()) < minute,
      };
    }
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    case INITIATE_INQUIRY_REQUEST:
      return { ...state, initiateInquiryInProgress: true, initiateInquiryError: null };
    case INITIATE_INQUIRY_SUCCESS:
      return { ...state, initiateInquiryInProgress: false };
    case INITIATE_INQUIRY_ERROR:
      return { ...state, initiateInquiryInProgress: false, initiateInquiryError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });

const initiateOrderSuccess = order => ({
  type: INITIATE_ORDER_SUCCESS,
  payload: order,
});

const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });

const confirmPaymentSuccess = orderId => ({
  type: CONFIRM_PAYMENT_SUCCESS,
  payload: orderId,
});

const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

export const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });

export const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});

export const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
export const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
export const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

export const initiateInquiryRequest = () => ({ type: INITIATE_INQUIRY_REQUEST });
export const initiateInquirySuccess = () => ({ type: INITIATE_INQUIRY_SUCCESS });
export const initiateInquiryError = e => ({
  type: INITIATE_INQUIRY_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const fetchCustomOfferCheckoutData = ({
  transactionId,
  customOfferId,
  listingId,
  config,
}) => async (dispatch, getState, sdk) => {
  const txId = safeId(transactionId);
  const offerId = safeId(customOfferId);

  debugCustomOfferCheckout('direct checkout restore requested', {
    transactionId: txId,
    customOfferId: offerId,
    routeListingId: safeId(listingId),
  });

  if (!txId || !offerId) {
    throw new Error('Missing transactionId or customOfferId for custom offer checkout');
  }

  const response = await sdk.transactions.show(
    {
      id: txId,
      include: [
        'customer',
        'customer.profileImage',
        'provider',
        'provider.profileImage',
        'listing',
        'listing.images',
        'booking',
      ],
    },
    { expand: true }
  );
  const entities = denormalisedResponseEntities(response);
  const transaction = entities[0];
  const offer = getCustomOfferById(transaction, offerId);

  if (!transaction?.id || !offer) {
    throw new Error('Accepted custom offer checkout data was not found');
  }

  const listingFromTransaction = transaction.listing || null;
  const listing = listingFromTransaction
    ? {
        ...listingFromTransaction,
        author: listingFromTransaction.author || transaction.provider,
      }
    : null;

  if (!listing?.id) {
    throw new Error('Accepted custom offer listing data was not found');
  }

  debugCustomOfferCheckout('direct checkout restore loaded', {
    transactionId: txId,
    customOfferId: offerId,
    listingId: listing.id.uuid,
    lastTransition: transaction.attributes?.lastTransition,
    processName: transaction.attributes?.processName,
    bookingStart: offer.startDateTime,
    bookingEnd: offer.endDateTime,
  });

  return buildCustomOfferCheckoutInitialValues({ transaction, listing, offer, config });
};

export const fetchResumePaymentCheckoutData = ({ transactionId }) => async (
  dispatch,
  getState,
  sdk
) => {
  const txId = safeId(transactionId);

  if (!txId) {
    throw new Error('Missing transactionId for resumed payment checkout');
  }

  const response = await sdk.transactions.show(
    {
      id: txId,
      include: [
        'customer',
        'customer.profileImage',
        'provider',
        'provider.profileImage',
        'listing',
        'listing.images',
        'booking',
      ],
    },
    { expand: true }
  );
  const entities = denormalisedResponseEntities(response);
  const transaction = entities[0];
  const listingFromTransaction = transaction?.listing || null;
  const listing = listingFromTransaction
    ? {
        ...listingFromTransaction,
        author: listingFromTransaction.author || transaction.provider,
      }
    : null;

  if (!transaction?.id || !listing?.id) {
    throw new Error('Resumed payment checkout data was not found');
  }

  return buildResumePaymentCheckoutValues({ transaction, listing });
};

export const loadData = (params, search, config) => dispatch => {
  const { transactionId, customOfferId, [RESUME_PAYMENT_PARAM]: resumePayment } = parse(
    search || ''
  );
  const isResumePayment = isResumePaymentRequested(resumePayment);

  if (isResumePayment && transactionId) {
    return dispatch(fetchResumePaymentCheckoutData({ transactionId })).then(initialValues =>
      dispatch(setInitialValues(initialValues))
    );
  }

  if (!transactionId || !customOfferId) {
    return Promise.resolve();
  }

  return dispatch(
    fetchCustomOfferCheckoutData({
      transactionId,
      customOfferId,
      listingId: params?.id,
      config,
    })
  ).then(initialValues => dispatch(setInitialValues(initialValues)));
};

export const initiateOrder = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  const state = getState();
  const currentUser = state?.user?.currentUser;
  const listingProcessAlias =
    processAlias || state?.CheckoutPage?.listing?.attributes?.publicData?.transactionProcessAlias;
  const processName = resolveLatestProcessName((listingProcessAlias || '').split('/')[0]);
  const isBookingListing = isBookingProcess(processName);
  if (isBookingListing && isHostUser(currentUser)) {
    const hostBookingBlockedError = createHostBookingBlockedError();
    dispatch(initiateOrderError(hostBookingBlockedError));
    return Promise.reject(hostBookingBlockedError);
  }

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { deliveryMethod } : {};

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  const customOffer = transitionParams?.protectedData?.customOffer;

  if (customOffer?.id) {
    debugCustomOfferCheckout('payment transition request', {
      transactionId: safeId(transactionId),
      customOfferId: customOffer.id,
      listingId: safeId(orderParams.listingId),
      bookingStart: transitionParams.bookingStart,
      bookingEnd: transitionParams.bookingEnd,
      currentTransition: state?.CheckoutPage?.transaction?.attributes?.lastTransition,
      transition: transitionName,
      processAlias,
      paymentMethod: transitionParams?.protectedData?.paymentMethod,
    });
  }

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    if (customOffer?.id) {
      debugCustomOfferCheckout('payment transition response', {
        transactionId: safeId(order?.id),
        customOfferId: customOffer.id,
        lastTransition: order?.attributes?.lastTransition,
        lineItems: order?.attributes?.lineItems?.map(item => item.code),
      });
    }
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    if (customOffer?.id) {
      debugCustomOfferCheckout('payment transition error response', {
        transactionId: safeId(transactionId),
        customOfferId: customOffer.id,
        status: e?.status,
        statusText: e?.statusText,
        errors: e?.data?.errors,
        message: e?.message,
      });
    }
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: safeId(orderParams.listingId),
      processAlias: listingProcessAlias || processAlias || null,
      transition: transitionName,
      isInquiryFlow: !!transactionId,
      ...getBookingDebugData(transitionParams),
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

export const confirmPayment = (transactionId, transitionName, transitionParams = {}) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(confirmPaymentRequest());

  const bodyParams = {
    id: transactionId,
    transition: transitionName,
    params: transitionParams,
  };
  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };

  return sdk.transactions
    .transition(bodyParams, queryParams)
    .then(async response => {
      const order = response.data.data;
      dispatch(confirmPaymentSuccess(order.id));
      await completeCustomOfferPaymentIfNeeded({ order, transactionId, transitionName });
      return order;
    })
    .catch(e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate transaction against default-inquiry process
 * Note: At this point inquiry transition is made directly against Marketplace API.
 *       So, client app's server is not involved here unlike with transitions including payments.
 *
 * @param {*} inquiryParams contains listingId and protectedData
 * @param {*} processAlias 'default-inquiry/release-1'
 * @param {*} transitionName 'transition/inquire-without-payment'
 * @returns
 */
export const initiateInquiryWithoutPayment = (inquiryParams, processAlias, transitionName) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(initiateInquiryRequest());

  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    const listing = getState()?.CheckoutPage?.listing;
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(initiateInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const bodyParams = {
    transition: transitionName,
    processAlias,
    params: inquiryParams,
  };
  const queryParams = {
    include: ['provider'],
    expand: true,
  };

  return sdk.transactions
    .initiate(bodyParams, queryParams)
    .then(response => {
      const transactionId = response.data.data.id;
      dispatch(initiateInquirySuccess());
      return transactionId;
    })
    .catch(e => {
      dispatch(initiateInquiryError(storableError(e)));
      throw e;
    });
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * The API allows us to do speculative transaction initiation and
 * transitions. This way we can create a test transaction and get the
 * actual pricing information as if the transaction had been started,
 * without affecting the actual data.
 *
 * We store this speculative transaction in the page store and use the
 * pricing info for the booking breakdown to get a proper estimate for
 * the price with the chosen information.
 */
export const speculateTransaction = (
  orderParams,
  processAlias,
  transactionId,
  transitionName,
  isPrivilegedTransition
) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;

  const { deliveryMethod, quantity, bookingDates, ...otherOrderParams } = orderParams;
  const quantityMaybe = quantity ? { stockReservationQuantity: quantity } : {};
  const bookingParamsMaybe = bookingDates || {};

  // Parameters only for client app's server
  const orderData = deliveryMethod ? { deliveryMethod } : {};

  // Parameters for Marketplace API
  const transitionParams = {
    ...quantityMaybe,
    ...bookingParamsMaybe,
    ...otherOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? {
        id: transactionId,
        transition: transitionName,
        params: transitionParams,
      }
    : {
        processAlias,
        transition: transitionName,
        params: transitionParams,
      };

  const queryParams = {
    include: ['booking', 'provider'],
    expand: true,
  };
  const customOffer = transitionParams?.protectedData?.customOffer;

  if (customOffer?.id) {
    debugCustomOfferCheckout('breakdown API request', {
      transactionId: safeId(transactionId),
      customOfferId: customOffer.id,
      listingId: safeId(transitionParams.listingId),
      bookingStart: transitionParams.bookingStart,
      bookingEnd: transitionParams.bookingEnd,
      currentTransition: getState()?.CheckoutPage?.transaction?.attributes?.lastTransition,
      transition: transitionName,
      processAlias,
      isPrivilegedTransition,
    });
  }

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    if (customOffer?.id) {
      debugCustomOfferCheckout('breakdown API response', {
        transactionId: safeId(tx?.id),
        customOfferId: customOffer.id,
        lastTransition: tx?.attributes?.lastTransition,
        lineItems: tx?.attributes?.lineItems?.map(item => item.code),
        payinTotal: tx?.attributes?.payinTotal?.amount,
      });
    }
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    if (customOffer?.id) {
      debugCustomOfferCheckout('breakdown API error response', {
        transactionId: safeId(transactionId),
        customOfferId: customOffer.id,
        status: e?.status,
        statusText: e?.statusText,
        errors: e?.data?.errors,
        message: e?.message,
      });
    }
    log.error(e, 'speculate-transaction-failed', {
      listingId: safeId(transitionParams.listingId),
      processAlias,
      transition: transitionName,
      isInquiryFlow: !!transactionId,
      isPrivilegedTransition: !!isPrivilegedTransition,
      ...getBookingDebugData(transitionParams),
      ...quantityMaybe,
      ...bookingParamsMaybe,
      ...orderData,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: true, orderData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const stripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());
  const fetchCurrentUserOptions = {
    callParams: { include: ['stripeCustomer.defaultPaymentMethod'] },
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions))
    .then(response => {
      dispatch(stripeCustomerSuccess());
    })
    .catch(e => {
      dispatch(stripeCustomerError(storableError(e)));
    });
};
